import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';

/**
 * Home-service vendors that serve Babcock Ranch.
 *
 * Candidate names came from resident recommendations in local Facebook
 * groups, then each was verified independently against the company's own
 * site or a directory listing. Only businesses that publish an explicit
 * Babcock Ranch / 33982 service claim (or are physically local) AND have a
 * working phone number are listed.
 *
 * The page is deliberately honest about how thin that verification is:
 * license numbers are self-published, not state-confirmed, and a resident
 * recommendation is an opinion. Presenting these as vetted endorsements
 * would be the wrong call for something people use to let a stranger into
 * their house.
 */

interface ServiceVendor {
  id: number;
  slug: string;
  name: string;
  category: string;
  phone: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  serves_babcock_ranch: boolean;
  license: string | null;
  hours_note: string | null;
  notes: string | null;
  source_urls: string[];
}

function telHref(phone: string): string {
  // Take the first number when a vendor publishes several.
  const first = phone.split(';')[0];
  return `tel:${first.replace(/[^\d+]/g, '')}`;
}

export function ServicesPage() {
  const [vendors, setVendors] = useState<ServiceVendor[] | null>(null);
  const [error, setError] = useState(false);
  const [category, setCategory] = useState<string>('all');

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ vendors: ServiceVendor[] }>('/vendors')
      .then((d) => {
        if (!cancelled) setVendors(d.vendors ?? []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const set = new Set((vendors ?? []).map((v) => v.category));
    return ['all', ...Array.from(set).sort()];
  }, [vendors]);

  const grouped = useMemo(() => {
    const list = (vendors ?? []).filter((v) => category === 'all' || v.category === category);
    const out: { category: string; items: ServiceVendor[] }[] = [];
    for (const v of list) {
      const last = out[out.length - 1];
      if (last && last.category === v.category) last.items.push(v);
      else out.push({ category: v.category, items: [v] });
    }
    return out;
  }, [vendors, category]);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Home Services</h1>
        <p className="page-subtitle">Trades that serve Babcock Ranch</p>
      </div>

      {/* Wrapping chip row — never horizontal-scroll. */}
      <div className="chip-row" role="tablist" aria-label="Trade">
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={category === c}
            className={`chip ${category === c ? 'chip--active' : ''}`}
            onClick={() => setCategory(c)}
          >
            {c === 'all' ? 'All' : c}
          </button>
        ))}
      </div>

      <div className="card" style={{ marginTop: 10 }}>
        <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--color-ink-muted)' }}>
          Starting points, not endorsements. These names came up in resident groups and were then
          checked against each company&apos;s own listing. Licence numbers shown are as published by
          the business, not confirmed against the state register. Always confirm licence, insurance
          and a quote before hiring.
        </p>
      </div>

      {error && (
        <div className="card">
          <p style={{ color: 'var(--color-ink-muted)' }}>Service list is temporarily unavailable.</p>
        </div>
      )}

      {!error && !vendors && (
        <div className="card">
          <div className="skeleton" style={{ height: 80 }} />
        </div>
      )}

      {vendors && vendors.length === 0 && (
        <div className="empty-state">
          <p>No vendors listed yet.</p>
        </div>
      )}

      {grouped.map((g) => (
        <div key={g.category} style={{ marginTop: 14 }}>
          <div className="section-title">{g.category}</div>
          {g.items.map((v) => (
            <div key={v.id} className="card" style={{ marginTop: 8 }}>
              <h2 style={{ fontSize: 16, margin: 0 }}>{v.name}</h2>

              <p style={{ fontSize: 13, color: 'var(--color-ink-muted)', marginTop: 4 }}>
                {v.city ?? v.address ?? 'Service-area business'}
                {v.serves_babcock_ranch && ' · lists Babcock Ranch in its service area'}
              </p>

              {v.license && (
                <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 4 }}>
                  Licence (as published): {v.license}
                </p>
              )}

              {v.hours_note && (
                <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 4 }}>
                  {v.hours_note}
                </p>
              )}

              <div className="action-row" style={{ marginTop: 10 }}>
                {v.phone && (
                  <a className="action-btn" href={telHref(v.phone)}>
                    Call
                  </a>
                )}
                {v.website && (
                  <a className="action-btn" href={v.website} target="_blank" rel="noreferrer">
                    Website
                  </a>
                )}
              </div>

              {v.phone && (
                <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 8 }}>
                  {v.phone}
                </p>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
